import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  Divider,
  Tooltip,
  CircularProgress,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Card,
  CardContent,
  IconButton,
  Snackbar
} from "@mui/material";
import axios from "axios";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PayrollSettings = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Available banks list
  const [banks, setBanks] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    bank_template_id: "",
    account_holder_name: "",
    account_number: "",
    ifsc_code: "",
    branch_name: "",
    transfer_type: "employer_bank"
  });
  
  // Existing settings (for edit mode)
  const [existingSettings, setExistingSettings] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Validation errors
  const [errors, setErrors] = useState({});

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setFetchError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setFetchError("No authentication token found. Please log in.");
        setLoading(false);
        return;
      }

      // Fetch all available banks
      const banksResponse = await axios.get(`${API_URL}/api/payroll-settings/banks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (banksResponse.data.success) {
        setBanks(banksResponse.data.data);
      }

      // Fetch existing payroll settings for the company
      try {
        const settingsResponse = await axios.get(`${API_URL}/api/payroll-settings/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (settingsResponse.data.success && settingsResponse.data.data) {
          const settings = settingsResponse.data.data;
          setExistingSettings(settings);
          setIsEditing(true);
          
          // Populate form with existing data
          setFormData({
            bank_template_id: settings.bank_template_id || "",
            account_holder_name: settings.account_holder_name || "",
            account_number: settings.account_number || "",
            ifsc_code: settings.ifsc_code || "",
            branch_name: settings.branch_name || "",
            transfer_type: settings.transfer_type || "employer_bank"
          });
        }
      } catch (settingsError) {
        // 404 means no settings found - that's fine for new companies
        if (settingsError.response && settingsError.response.status === 404) {
        } else {
        }
      }

    } catch (error) {
      setFetchError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.bank_template_id) {
      newErrors.bank_template_id = "Please select a bank.";
    }

    if (!formData.account_holder_name) {
      newErrors.account_holder_name = "Account holder name is required.";
    }

    if (!formData.account_number) {
      newErrors.account_number = "Account number is required.";
    } else if (!/^\d{9,18}$/.test(formData.account_number)) {
      newErrors.account_number = "Account number must be 9-18 digits.";
    }

    if (!formData.ifsc_code) {
      newErrors.ifsc_code = "IFSC code is required.";
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc_code)) {
      newErrors.ifsc_code = "Invalid IFSC format. (e.g., HDFC0001234)";
    }

    if (!formData.transfer_type) {
      newErrors.transfer_type = "Please select transfer type.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        setSubmitting(false);
        return;
      }

      let response;
      if (isEditing && existingSettings) {
        // Update existing settings
        response = await axios.put(
          `${API_URL}/api/payroll-settings/settings`,
          formData,
          {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        // Create new settings
        response = await axios.post(
          `${API_URL}/api/payroll-settings/settings`,
          formData,
          {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }

      if (response.data.success) {
        setSuccessMessage(
          isEditing 
            ? "Payroll settings updated successfully!" 
            : "Payroll settings saved successfully!"
        );
        
        // Update existing settings for edit mode
        if (response.data.data) {
          setExistingSettings(response.data.data);
          setIsEditing(true);
        }
        
        // Clear any previous errors
        setErrors({});
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
      }

    } catch (error) {
      
      let errorMessage = "Failed to save payroll settings. Please try again.";
      if (error.response && error.response.data) {
        errorMessage = error.response.data.message || errorMessage;
      }
      
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    if (existingSettings) {
      setFormData({
        bank_template_id: existingSettings.bank_template_id || "",
        account_holder_name: existingSettings.account_holder_name || "",
        account_number: existingSettings.account_number || "",
        ifsc_code: existingSettings.ifsc_code || "",
        branch_name: existingSettings.branch_name || "",
        transfer_type: existingSettings.transfer_type || "employer_bank"
      });
    } else {
      setFormData({
        bank_template_id: "",
        account_holder_name: "",
        account_number: "",
        ifsc_code: "",
        branch_name: "",
        transfer_type: "employer_bank"
      });
    }
    setErrors({});
  };

  const getBankName = (bankId) => {
    const bank = banks.find(b => b.id === bankId);
    return bank ? bank.bank_name : "";
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Box sx={{ maxWidth: 800, margin: 'auto', padding: 3 }}>
        <Alert severity="error">{fetchError}</Alert>
        <Button 
          variant="contained" 
          onClick={fetchData} 
          sx={{ mt: 2 }}
          startIcon={<RefreshIcon />}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, margin: 'auto', padding: 3 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        {isEditing ? "Edit Payroll Settings" : "Company Payroll Settings"}
      </Typography>

      {isEditing && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You are editing existing payroll settings for your company.
          {existingSettings?.bankTemplate && (
            <span> Current bank: <strong>{existingSettings.bankTemplate.bank_name}</strong></span>
          )}
        </Alert>
      )}

      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setSuccessMessage(null)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          <CheckCircleIcon sx={{ mr: 1 }} />
          {successMessage}
        </Alert>
      )}

      <Paper elevation={3} sx={{ padding: 4 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom color="primary">
            Company Bank Details
          </Typography>
          <Divider sx={{ marginBottom: 3 }} />

          <Grid container spacing={3}>
            {/* Bank Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.bank_template_id}>
                <InputLabel id="bank-select-label">Select Bank *</InputLabel>
                <Select
                  labelId="bank-select-label"
                  name="bank_template_id"
                  value={formData.bank_template_id}
                  onChange={handleChange}
                  label="Select Bank *"
                >
                  <MenuItem value="">
                    <em>-- Select a bank --</em>
                  </MenuItem>
                  {banks.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {bank.bank_name} ({bank.bank_code})
                    </MenuItem>
                  ))}
                </Select>
                {errors.bank_template_id && (
                  <FormHelperText>{errors.bank_template_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Account Holder Name */}
            <Grid item xs={12}>
              <Tooltip title="Enter the name as it appears on the company bank account" placement="top" arrow>
                <TextField
                  label="Account Holder Name *"
                  name="account_holder_name"
                  value={formData.account_holder_name}
                  onChange={handleChange}
                  error={!!errors.account_holder_name}
                  helperText={errors.account_holder_name}
                  fullWidth
                  required
                />
              </Tooltip>
            </Grid>

            {/* Account Number */}
            <Grid item xs={12} md={6}>
              <Tooltip title="Enter the company bank account number (9-18 digits)" placement="top" arrow>
                <TextField
                  label="Account Number *"
                  name="account_number"
                  value={formData.account_number}
                  onChange={handleChange}
                  error={!!errors.account_number}
                  helperText={errors.account_number || "Must be 9-18 digits"}
                  fullWidth
                  required
                />
              </Tooltip>
            </Grid>

            {/* IFSC Code */}
            <Grid item xs={12} md={6}>
              <Tooltip title="Enter the IFSC code of the bank branch (e.g., HDFC0001234)" placement="top" arrow>
                <TextField
                  label="IFSC Code *"
                  name="ifsc_code"
                  value={formData.ifsc_code}
                  onChange={handleChange}
                  error={!!errors.ifsc_code}
                  helperText={errors.ifsc_code || "Format: 4 letters + 0 + 6 digits"}
                  fullWidth
                  required
                  placeholder="e.g., HDFC0001234"
                />
              </Tooltip>
            </Grid>

            {/* Branch Name */}
            <Grid item xs={12}>
              <Tooltip title="Enter the branch name where the bank account is held" placement="top" arrow>
                <TextField
                  label="Branch Name"
                  name="branch_name"
                  value={formData.branch_name}
                  onChange={handleChange}
                  fullWidth
                  placeholder="e.g., Mumbai Main Branch"
                />
              </Tooltip>
            </Grid>
          </Grid>

          {/* <Divider sx={{ my: 4 }} />

          <Typography variant="h6" gutterBottom color="primary">
            Transfer Settings
          </Typography> */}
          <Divider sx={{ marginBottom: 3 }} />

          {/* <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.transfer_type}>
                <InputLabel id="transfer-type-label">Transfer Type *</InputLabel>
                <Select
                  labelId="transfer-type-label"
                  name="transfer_type"
                  value={formData.transfer_type}
                  onChange={handleChange}
                  label="Transfer Type *"
                >
                  <MenuItem value="employer_bank">
                    By Employer Bank (One file for all employees)
                  </MenuItem>
                  <MenuItem value="employee_bank">
                    By Employee Bank (Separate files per bank)
                  </MenuItem>
                </Select>
                {errors.transfer_type && (
                  <FormHelperText>{errors.transfer_type}</FormHelperText>
                )}
                <FormHelperText>
                  <strong>Employer Bank:</strong> One file with all employees uploaded to your bank.
                  <br />
                  <strong>Employee Bank:</strong> Separate files per bank uploaded to respective banks.
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid> */}

          {/* Summary Card */}
          {/* {formData.bank_template_id && (
            <Card sx={{ mt: 4, backgroundColor: '#f5f5f5' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Summary
                </Typography>
                <Typography variant="body2">
                  <strong>Bank:</strong> {getBankName(formData.bank_template_id) || "Not selected"}
                </Typography>
                <Typography variant="body2">
                  <strong>Account Holder:</strong> {formData.account_holder_name || "Not entered"}
                </Typography>
                <Typography variant="body2">
                  <strong>Transfer Type:</strong> {
                    formData.transfer_type === 'employer_bank' 
                      ? 'By Employer Bank (One file)' 
                      : 'By Employee Bank (Separate files)'
                  }
                </Typography>
              </CardContent>
            </Card>
          )} */}

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, marginTop: 4 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
              sx={{ minWidth: 150 }}
            >
              {submitting ? "Saving..." : (isEditing ? "Update Settings" : "Save Settings")}
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              onClick={handleReset}
              disabled={submitting}
              sx={{ minWidth: 150 }}
            >
              Reset
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Help Section */}
      {/* <Paper elevation={1} sx={{ mt: 3, padding: 3, backgroundColor: '#fafafa' }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          ℹ️ Need Help?
        </Typography>
        <Typography variant="body2" color="textSecondary">
          <strong>What is this?</strong> Configure your company's bank details for salary disbursement.
          <br />
          <strong>Employer Bank:</strong> All employee salaries are transferred from this bank account.
          <br />
          <strong>Employee Bank:</strong> Employees can have accounts in different banks, and you upload separate files.
          <br />
          <strong>Need to change?</strong> You can update these settings anytime. Changes will apply to future salary exports.
        </Typography>
      </Paper> */}

      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSuccessMessage(null)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PayrollSettings;